import { __read, __spreadArray } from "tslib";
import { getEasingFunction, convertToDash } from './custom-easing';
import { bezier } from './bezier-easing';
export var linear = function linear(x) {
  return x;
};
var Start = 1;
var Middle = 0.5;
var End = 0;

function step(count, pos) {
  return function (x) {
    if (x >= 1) {
      return 1;
    }

    var stepSize = 1 / count;
    x += pos * stepSize;
    return x - x % stepSize;
  };
}

var numberString = '\\s*(-?\\d+\\.?\\d*|-?\\.\\d+)\\s*';
var cubicBezierRe = new RegExp('cubic-bezier\\(' + numberString + ',' + numberString + ',' + numberString + ',' + numberString + '\\)');
var step1Re = /steps\(\s*(\d+)\s*\)/;
var step2Re = /steps\(\s*(\d+)\s*,\s*(start|middle|end)\s*\)/;
export function parseEasingFunction(normalizedEasing) {
  var cubicData = cubicBezierRe.exec(normalizedEasing);

  if (cubicData) {
    // @ts-ignore
    return bezier.apply(void 0, __spreadArray([], __read(cubicData.slice(1).map(Number)), false));
  }

  var step1Data = step1Re.exec(normalizedEasing);

  if (step1Data) {
    return step(Number(step1Data[1]), End);
  }

  var step2Data = step2Re.exec(normalizedEasing);

  if (step2Data) {
    // @ts-ignore
    return step(Number(step2Data[1]), {
      start: Start,
      middle: Middle,
      end: End
    }[step2Data[2]]);
  }

  return getEasingFunction(normalizedEasing);
}
export function calculateActiveDuration(timing) {
  // @ts-ignore
  return Math.abs(repeatedDuration(timing) / (timing.playbackRate || 1));
}

function repeatedDuration(timing) {
  var _a; // https://drafts.csswg.org/web-animations/#calculating-the-active-duration


  if (timing.duration === 0 || timing.iterations === 0) {
    return 0;
  } // @see https://developer.mozilla.org/en-US/docs/Web/API/EffectTiming/duration#value
  // if (timing.duration === 'auto') {
  //   timing.duration = 0;
  // }


  return (timing.duration === 'auto' ? 0 : Number(timing.duration)) * ((_a = timing.iterations) !== null && _a !== void 0 ? _a : 1);
}

var PhaseNone = 0;
var PhaseBefore = 1;
var PhaseAfter = 2;
var PhaseActive = 3;

function calculatePhase(activeDuration, localTime, timing) {
  // https://drafts.csswg.org/web-animations/#animation-effect-phases-and-states
  if (localTime === null) {
    return PhaseNone;
  }

  var endTime = timing.delay + activeDuration + timing.endDelay;

  if (localTime < Math.min(timing.delay, endTime)) {
    return PhaseBefore;
  }

  if (localTime >= Math.min(timing.delay + activeDuration, endTime)) {
    return PhaseAfter;
  }

  return PhaseActive;
}

function calculateActiveTime(activeDuration, fillMode, localTime, phase, delay) {
  // https://drafts.csswg.org/web-animations/#calculating-the-active-time
  switch (phase) {
    case PhaseBefore:
      if (fillMode === 'backwards' || fillMode === 'both') return 0;
      return null;

    case PhaseActive:
      return localTime - delay;

    case PhaseAfter:
      if (fillMode === 'forwards' || fillMode === 'both') return activeDuration;
      return null;

    case PhaseNone:
      return null;
  }
}

function calculateOverallProgress(iterationDuration, phase, iterations, activeTime, iterationStart) {
  // https://drafts.csswg.org/web-animations/#calculating-the-overall-progress
  var overallProgress = iterationStart;

  if (iterationDuration === 0) {
    if (phase !== PhaseBefore) {
      overallProgress += iterations;
    }
  } else {
    overallProgress += activeTime / iterationDuration;
  }

  return overallProgress;
}

function calculateSimpleIterationProgress(overallProgress, iterationStart, phase, iterations, activeTime, iterationDuration) {
  // https://drafts.csswg.org/web-animations/#calculating-the-simple-iteration-progress
  var simpleIterationProgress = overallProgress === Infinity ? iterationStart % 1 : overallProgress % 1;

  if (simpleIterationProgress === 0 && phase === PhaseAfter && iterations !== 0 && (activeTime !== 0 || iterationDuration === 0)) {
    simpleIterationProgress = 1;
  }

  return simpleIterationProgress;
}

function calculateCurrentIteration(phase, iterations, simpleIterationProgress, overallProgress) {
  // https://drafts.csswg.org/web-animations/#calculating-the-current-iteration
  if (phase === PhaseAfter && iterations === Infinity) {
    return Infinity;
  }

  if (simpleIterationProgress === 1) {
    return Math.floor(overallProgress) - 1;
  }

  return Math.floor(overallProgress);
}

function calculateDirectedProgress(playbackDirection, currentIteration, simpleIterationProgress) {
  // https://drafts.csswg.org/web-animations/#calculating-the-directed-progress
  var currentDirection = playbackDirection;

  if (playbackDirection !== 'normal' && playbackDirection !== 'reverse') {
    var d = currentIteration;

    if (playbackDirection === 'alternate-reverse') {
      d += 1;
    }

    currentDirection = 'normal';

    if (d !== Infinity && d % 2 !== 0) {
      currentDirection = 'reverse';
    }
  }

  if (currentDirection === 'normal') {
    return simpleIterationProgress;
  }

  return 1 - simpleIterationProgress;
}

export function calculateIterationProgress(activeDuration, localTime, timing) {
  var phase = calculatePhase(activeDuration, localTime, timing);
  var activeTime = calculateActiveTime(activeDuration, timing.fill, localTime, phase, timing.delay);
  if (activeTime === null) return null;
  var duration = timing.duration === 'auto' ? 0 : timing.duration;
  var overallProgress = calculateOverallProgress(duration, phase, timing.iterations, activeTime, timing.iterationStart);
  var simpleIterationProgress = calculateSimpleIterationProgress(overallProgress, timing.iterationStart, phase, timing.iterations, activeTime, duration);
  var currentIteration = calculateCurrentIteration(phase, timing.iterations, simpleIterationProgress, overallProgress);
  var directedProgress = calculateDirectedProgress(timing.direction, currentIteration, simpleIterationProgress);
  timing.currentIteration = currentIteration;
  timing.progress = directedProgress; // https://drafts.csswg.org/web-animations/#calculating-the-transformed-progress
  // https://drafts.csswg.org/web-animations/#calculating-the-iteration-progress

  return timing.easingFunction(directedProgress);
}
/**
 * From: [https://easings.net]
 * Read More about easings on [MDN](https://developer.mozilla.org/en-US/docs/Web/API/EffectTiming/easing)
 */

export var EASINGS = {
  in: 'ease-in',
  out: 'ease-out',
  'in-out': 'ease-in-out',
  // Sine
  'in-sine': 'cubic-bezier(0.47, 0, 0.745, 0.715)',
  'out-sine': 'cubic-bezier(0.39, 0.575, 0.565, 1)',
  'in-out-sine': 'cubic-bezier(0.445, 0.05, 0.55, 0.95)',
  // Quad
  'in-quad': 'cubic-bezier(0.55, 0.085, 0.68, 0.53)',
  'out-quad': 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  'in-out-quad': 'cubic-bezier(0.455, 0.03, 0.515, 0.955)',
  // Cubic
  'in-cubic': 'cubic-bezier(0.55, 0.055, 0.675, 0.19)',
  'out-cubic': 'cubic-bezier(0.215, 0.61, 0.355, 1)',
  'in-out-cubic': 'cubic-bezier(0.645, 0.045, 0.355, 1)',
  // Quart
  'in-quart': 'cubic-bezier(0.895, 0.03, 0.685, 0.22)',
  'out-quart': 'cubic-bezier(0.165, 0.84, 0.44, 1)',
  'in-out-quart': 'cubic-bezier(0.77, 0, 0.175, 1)',
  // Quint
  'in-quint': 'cubic-bezier(0.755, 0.05, 0.855, 0.06)',
  'out-quint': 'cubic-bezier(0.23, 1, 0.32, 1)',
  'in-out-quint': 'cubic-bezier(0.86, 0, 0.07, 1)',
  // Expo
  'in-expo': 'cubic-bezier(0.95, 0.05, 0.795, 0.035)',
  'out-expo': 'cubic-bezier(0.19, 1, 0.22, 1)',
  'in-out-expo': 'cubic-bezier(1, 0, 0, 1)',
  // Circ
  'in-circ': 'cubic-bezier(0.6, 0.04, 0.98, 0.335)',
  'out-circ': 'cubic-bezier(0.075, 0.82, 0.165, 1)',
  'in-out-circ': 'cubic-bezier(0.785, 0.135, 0.15, 0.86)',
  // Back
  'in-back': 'cubic-bezier(0.6, -0.28, 0.735, 0.045)',
  'out-back': 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  'in-out-back': 'cubic-bezier(0.68, -0.55, 0.265, 1.55)'
};
export var EasingKeys = Object.keys(EASINGS);
/**
 * Converts users input into a usable easing function string
 */

export var getEase = function getEase(ease) {
  if (ease === void 0) {
    ease = 'ease';
  } // Convert camelCase strings into dashed strings, then Remove the "ease-" keyword


  var search = convertToDash(ease).replace(/^ease-/, '');
  return EASINGS[search] || ease;
};